import { createContext, useContext, useReducer } from 'react';

import { PAYMENT_ACTIONS, PAYMENT_STATUS } from '../constants/payment';

const PaymentStateContext = createContext(null);
PaymentStateContext.displayName = 'PaymentStateContext';
const PaymentDispatchContext = createContext(() => null);
PaymentDispatchContext.displayName = 'PaymentDispatchContext';

// Initial state for payment reducer
const INITIAL_PAYMENT_STATE = {
  cardToken: null,
  error: null,
  paymentMethodId: null,
  paymentMethodType: null,
  status: PAYMENT_STATUS.IDLE
};

// Reducer function for payment state management
function paymentReducer(state, action) {
  switch (action.type) {
    case PAYMENT_ACTIONS.PLACE_ORDER_START:
      return {
        ...state,
        error: null,
        status: PAYMENT_STATUS.PENDING
      };
    case PAYMENT_ACTIONS.PLACE_ORDER_SUCCESS:
      return {
        ...state,
        error: null,
        status: PAYMENT_STATUS.SUCCESS
      };
    case PAYMENT_ACTIONS.PLACE_ORDER_ERROR:
      return {
        ...state,
        error: action.payload,
        status: PAYMENT_STATUS.FAILED
      };
    case PAYMENT_ACTIONS.SET_PAYMENT_INSTRUMENT:
      return {
        ...state,
        cardToken: action.payload.cardToken,
        error: null,
        paymentMethodId: action.payload.paymentMethodId,
        paymentMethodType: action.payload.paymentMethodType || null,
        status: PAYMENT_STATUS.IDLE
      };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

export function PaymentProvider({ children }) {
  const [state, dispatch] = useReducer(paymentReducer, INITIAL_PAYMENT_STATE);

  return (
    <PaymentStateContext.Provider value={state}>
      <PaymentDispatchContext.Provider value={dispatch}>{children}</PaymentDispatchContext.Provider>
    </PaymentStateContext.Provider>
  );
}

export function usePaymentStateContext() {
  const context = useContext(PaymentStateContext);
  if (!context) {
    throw new Error('usePaymentStateContext hook must be used within a PaymentProvider');
  }
  return context;
}

export function usePaymentDispatchContext() {
  const context = useContext(PaymentDispatchContext);
  if (!context) {
    throw new Error('usePaymentDispatchContext hook must be used within a PaymentProvider');
  }
  return context;
}

export function usePaymentContext() {
  return [usePaymentStateContext(), usePaymentDispatchContext()];
}
